// Types
import { DegreeDiopterType } from "src/services/exams/types";

// Every figure in this report goes through these, so a null is always a dash
// and never a zero -- "measured as nothing" and "not measured" must not read
// the same way.
export const DASH = "—";
const NA = "N/A";
const MINUS = "-";

// A unit belongs to a number, so a missing number must not keep it: "N/A cm" reads as a
// measurement of nothing rather than as a measurement nobody took. Pass `unit` (e.g. "Δ", " cm")
// and it's appended only when there's an actual value to attach it to.
export const num = (
  value: number | null | undefined,
  digits = 1,
  unit = ""
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return NA;
  return (value < 0 ? MINUS : "") + Math.abs(value).toFixed(digits) + unit;
};

export const signed = (
  value: number | null | undefined,
  digits = 1
): string => {
  if (value === null || value === undefined) return DASH;
  return (
    (value > 0 ? "+" : value < 0 ? MINUS : "") + Math.abs(value).toFixed(digits)
  );
};

export const fraction = (
  value: number | null | undefined,
  digits = 0
): string =>
  value === null || value === undefined
    ? DASH
    : `${(value * 100).toFixed(digits)}%`;

export const pct = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined ? DASH : `${value.toFixed(digits)}%`;

export const interval = (
  measurement: DegreeDiopterType | null | undefined,
  digits = 1
): string | null => {
  if (!measurement?.ci95) return null;
  return `${num(measurement.ci95[0], digits)} to ${num(measurement.ci95[1], digits)}`;
};

export const withInterval = (
  measurement: DegreeDiopterType | null | undefined,
  suffix = "",
  digits = 1
): string => {
  if (!measurement) return DASH;
  const ci = interval(measurement, digits);
  const value = num(measurement.value, digits) + suffix;
  return ci ? `${value} (95% CI ${ci}${suffix})` : `${value} (no interval)`;
};
